<template>
	<!-- Vue 3 fragment root: a wrapper element would sit between #__nuxt and
	the layout and break its height CSS. -->
	<!-- eslint-disable-next-line vue/no-multiple-template-root -->
	<slot v-if="shellReady" />
	<div v-else class="auth-boot-gate" role="status" aria-busy="true" aria-label="Loading">
		<div class="auth-boot-gate__spinner" />
		<div class="auth-boot-gate__label">Loading<span class="auth-boot-gate__dots" /></div>
	</div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';

// Prerendered pages are generated without a user session, so the baked HTML
// must ship this loading state rather than the logged-out app shell — the
// shell would otherwise be visible while the client-side auth check runs
// against the backend. Real SSR verifies auth in the route middleware before
// rendering, so the shell is correct there and the flag serialises as true.
//
// The flip happens in onMounted: the router's initial navigation (and
// therefore verifyAuthentication in auth.global.ts) completes before the app
// mounts, so by then the user is either verified or already redirected to
// /login. Flipping any earlier on the client would mismatch the prerendered
// spinner during hydration.
const shellReady = useState<boolean>('lcp-shell-ready', () => !import.meta.prerender);

onMounted(() => {
	shellReady.value = true;
});
</script>

<style scoped lang="scss">
.auth-boot-gate {
	position: fixed;
	inset: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background: var(--background-25, #fafafa);
	font-family:
		-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

	&__spinner {
		width: 50px;
		height: 50px;
		border: 4px solid var(--background-100, #e0e0e0);
		border-top: 4px solid #3498db;
		border-radius: 50%;
		animation: auth-boot-gate-spin 1s linear infinite;
	}

	&__label {
		margin-top: 20px;
		font-size: 16px;
		color: var(--text-50, #161616);
		text-align: center;
	}

	&__dots {
		display: inline-block;
		width: 20px;
		text-align: left;

		&::after {
			content: '';
			animation: auth-boot-gate-dots 1.5s infinite;
		}
	}
}

@keyframes auth-boot-gate-spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@keyframes auth-boot-gate-dots {
	0%,
	20% {
		content: '';
	}
	40% {
		content: '.';
	}
	60% {
		content: '..';
	}
	80%,
	100% {
		content: '...';
	}
}
</style>
